import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Users, TrendingUp, Building2, Settings, Cloud, Shield, Monitor, AlertTriangle, UserCheck, Map, BarChart3, Database, Network, Cog } from 'lucide-react';
import { Button } from './ui/button';
import { serviceDetails } from '../mockDetails';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../locales/translations';
import Header from './Header';
import Footer from './Footer';

const ServiceDetail = () => {
  const { serviceSlug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const service = Object.values(serviceDetails).find((item) => item.slug === serviceSlug);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t(language, 'serviceDetail.notFound')}</h1>
          <Button onClick={() => navigate('/')}>{t(language, 'serviceDetail.backHome')}</Button>
        </div>
      </div>
    );
  }

  const scrollToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  const getFeatureIcon = (title = '') => {
    const normalized = title.toLowerCase();

    if (normalized.includes('desain') || normalized.includes('konstruksi')) return Building2;
    if (normalized.includes('modernisasi')) return Settings;
    if (normalized.includes('cloud') || normalized.includes('hybrid')) return Cloud;

    if (normalized.includes('keamanan jaringan')) return Shield;
    if (normalized.includes('endpoint') || normalized.includes('data')) return Monitor;
    if (normalized.includes('insiden') || normalized.includes('pemulihan')) return AlertTriangle;
    if (normalized.includes('terkelola')) return UserCheck;

    if (normalized.includes('optimalisasi')) return BarChart3;
    if (normalized.includes('lahan') || normalized.includes('spasial')) return Map;
    if (normalized.includes('kependudukan')) return Users;

    if (normalized.includes('database komprehensif')) return Database;
    if (normalized.includes('database terintegrasi')) return Network;
    if (normalized.includes('database cerdas') || normalized.includes('manajemen database')) return Cog;

    return Cog;
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={service.heroImage}
            alt={service.title}
            loading="eager"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/90 to-gray-900/80"></div>
          <div className="absolute -top-24 -left-24 w-72 h-72 bg-orange-500/20 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-24 right-10 w-96 h-96 bg-red-500/20 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="mb-8 bg-transparent border-white text-white hover:bg-white hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="mr-2" size={18} />
            {t(language, 'serviceDetail.back')}
          </Button>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-full text-sm font-semibold mb-6 border border-white/20 backdrop-blur">
              <span className="w-2 h-2 rounded-full bg-orange-400"></span>
              {t(language, 'serviceDetail.featured')}
            </div>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-[1.05]">
              {service.title}
            </h1>
            <p className="text-base sm:text-xl text-gray-300 leading-relaxed max-w-3xl">
              {language === 'en' ? (service.overviewEn || service.overview) : service.overview}
            </p>
          </div>
        </div>
      </section>

      {/* Main Features */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block px-3 py-1.5 bg-orange-50 text-orange-600 rounded-full text-xs font-semibold mb-4">
              {t(language, 'serviceDetail.valueHighlights')}
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              {t(language, 'serviceDetail.featuresOverview')} <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">{language === 'id' ? 'Utama' : 'Overview'}</span>
            </h2>
          </div>

          <div className="space-y-10">
            {service.mainFeatures.map((feature, index) => (
              <div key={index} className="bg-white rounded-3xl p-6 sm:p-8 shadow-lg border border-gray-100">
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center text-orange-600">
                    {(() => {
                      const FeatureIcon = getFeatureIcon(feature.title);
                      return <FeatureIcon size={20} />;
                    })()}
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600 text-base sm:text-lg">{feature.description}</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {feature.details.map((detail, idx) => (
                    <div key={idx} className="flex items-start p-4 bg-gray-50 rounded-xl hover:bg-orange-50 transition-colors border border-transparent hover:border-orange-100">
                      <CheckCircle2 className="text-orange-600 mr-3 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-700">{detail}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block px-3 py-1.5 bg-orange-50 text-orange-600 rounded-full text-xs font-semibold mb-4">
                {t(language, 'serviceDetail.benefits')}
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                {t(language, 'serviceDetail.benefitsForBusiness')} <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">{language === 'id' ? 'Utama' : 'Overview'}</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {service.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start p-6 bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl border border-orange-100 shadow-sm">
                  <TrendingUp className="text-orange-600 mr-4 flex-shrink-0" size={24} />
                  <span className="text-gray-900 font-medium">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Case Studies Removed */}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-red-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute -top-16 -left-16 w-64 h-64 bg-white/20 rounded-full blur-2xl"></div>
          <div className="absolute -bottom-20 right-10 w-72 h-72 bg-white/20 rounded-full blur-2xl"></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users size={64} className="mx-auto text-white mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            {t(language, 'serviceDetail.readyToImplement')}
          </h2>
          <p className="text-base sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            {t(language, 'serviceDetail.readyDesc')}
          </p>
          <Button
            size="lg"
            onClick={scrollToContact}
            className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold"
          >
            {t(language, 'serviceDetail.freeConsultation')}
            <ArrowLeft className="ml-2 rotate-180" size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetail;




